import React, { useState, useEffect } from 'react';
import { Card, Form, Dropdown, Button } from 'react-bootstrap';
import {
	BsPencilFill,
	BsTrash3,
	BsArrowsMove,
	BsFileEarmarkPost,
	BsThreeDotsVertical,
	BsHouseHeartFill,
	BsPlayCircleFill,
	BsImageFill,
} from 'react-icons/bs';
import { MdContentCopy } from 'react-icons/md';

import BayLoader from '../../BayLoader';
import BayTooltip from '../../BayTooltip';
import VisibilityIcon from '../../Icons/VisibilityIcon';
import {
	getIconByFileType,
	getFileType,
} from '../../utilities/fileExtensionHandler';

interface FileData {
	_id: string;
	_type: string;
	name: string;
	url?: string;
	mimeType?: string;
	isHidden?: boolean;
	presentOnHomepage?: boolean;
	thumbnailUrls?: {
		custom?: string;
		default?: string;
	};
	thumbnailUrl?: string;
	[key: string]: unknown;
}

interface CustomToggleProps {
	onClick: (e: React.MouseEvent) => void;
	setActiveActionId: () => void;
	currentId: string;
	activeActionId: string | null;
}

const CustomToggle = React.forwardRef<HTMLSpanElement, CustomToggleProps>(
	({ onClick, setActiveActionId, currentId, activeActionId }, ref) => (
		<BayTooltip overlay="Actions" placement="top">
			<span
				ref={ref}
				className={`card-action-dropdown-button text-primary cursor-pointer ${
					activeActionId === currentId ? 'bay-icon-editable active' : ''
				}`}
				onClick={(e) => {
					e.preventDefault();
					onClick(e);
					setActiveActionId();
				}}
			>
				<BsThreeDotsVertical size={16} />
			</span>
		</BayTooltip>
	),
);

interface FilesGridCardProps {
	t: (key: string) => string;
	file: FileData;
	index: number;
	isSearchView: boolean;
	selectedData: FileData[];
	dispatchSelect: (event: React.ChangeEvent<HTMLInputElement>, file: FileData) => void;
	activeActionId: string | null;
	setActiveActionId: (id: string | null) => void;
	handleOpenFolder: (file: FileData) => void;
	cardActionHandler: (action: string, file: FileData) => void;
	searchBreadcrumbs: (file: FileData) => React.ReactNode;
}

const FilesGridCard: React.FC<FilesGridCardProps> = ({
	t,
	file,
	index,
	isSearchView,
	selectedData,
	dispatchSelect,
	activeActionId,
	setActiveActionId,
	handleOpenFolder,
	cardActionHandler,
	searchBreadcrumbs,
}) => {
	const [checked, setChecked] = useState(
		selectedData
			? selectedData?.filter((e) => e._id === file._id)?.length > 0
			: false,
	);

	const isVideoFile = file?.mimeType?.includes('video');

	const imageSmushed = `https://imageproxy.services.bkat.io/?url=${encodeURIComponent(file?.url || '')}&h=168&fit=contain`;

	useEffect(() => {
		setChecked(
			selectedData
				? selectedData?.filter((e) => e._id === file._id)?.length > 0
				: false,
		);
	}, [selectedData, file._id]);

	const handleCardCheckClick = (event: React.ChangeEvent<HTMLInputElement>, file: FileData) => {
		setChecked(!checked);
		dispatchSelect(event, file);
	};

	const handleActionClicked = (eventKey: string | null, event: React.SyntheticEvent<Element>, cardData: FileData) => {
		event.stopPropagation();
		event.preventDefault();

		if (eventKey) {
			cardActionHandler(eventKey, cardData);
		}
	};

	return (
		<div className="col-hd-2 col-xxxl-3 col-xxl-4 col-xl-4 col-lg-4 col-sm-6 col-12">
			<Card
				key={index}
				className={`bay-files-card mb-0 shadow ${
					file._type === 'Entry$Folder' ? ' folder' : ''
				}${checked ? ' active' : ''}`}
			>
				{isSearchView && (
					<div className="px-4 pt-4 h-100">{searchBreadcrumbs(file)}</div>
				)}
				<div className="d-flex justify-content-between align-items-center px-4 pt-4 pb-3">
					<BayTooltip
						overlay={`Select ${
							file._type === 'Entry$File' ? 'File' : 'Folder'
						}`}
						placement="top"
					>
						<Form.Check
							checked={checked}
							className="bay-checkbox m-0"
							type="checkbox"
							onChange={(e) => {
								handleCardCheckClick(e, file);
								if (document.activeElement !== document.body) {
									(document.activeElement as HTMLElement).blur();
								}
							}}
						/>
					</BayTooltip>
					<div className="d-flex justify-content-end align-items-center">
						<span
							className={`me-2 pb-1 ${file.isHidden ? 'text-500' : 'text-700'}`}
						>
							{file?.isHidden && (
								<BayTooltip
									overlay={file?.isHidden && t('hidden')}
									placement="top"
								>
									<VisibilityIcon isHidden={file?.isHidden} />
								</BayTooltip>
							)}
								{/*
								  * Was a conditional `alignConfig` + `placement` keyed on
								  * `(index + 1) % divideFactor === 0` -- i.e. "if this card is
								  * last in its row, right-align the tooltip and pull it 20px
								  * left so it does not run off the viewport".
								  *
								  * That is hand-rolled viewport avoidance, and `shift({ padding: 8 })`
								  * in BayTooltip's middleware does it for every card
								  * unconditionally and by measurement rather than by arithmetic on
								  * the column count. So the condition, the alignConfig and
								  * `divideFactor`'s role here all go away.
								  *
								  * This is the better outcome than translating it: the original
								  * had to know how many columns the grid had.
								  */}
							{file?.presentOnHomepage && (
								<BayTooltip
									overlay={file?.presentOnHomepage && t('appearsOnHomePage')}
									overlayClassName="file-card-tooltip"
									overlayMaxWidth={325}
									placement="top"
								>
									<BsHouseHeartFill />
								</BayTooltip>
							)}
						</span>
						<Dropdown
							autoClose
							align="end"
							show={activeActionId === file._id}
							onSelect={(eventKey, event) => {
								const syntheticEvent = event as unknown as React.SyntheticEvent<Element>;
								handleActionClicked(eventKey, syntheticEvent, file);
							}}
							onToggle={() => setActiveActionId(null)}
						>
							<Dropdown.Toggle
								as={CustomToggle}
								activeActionId={activeActionId}
								currentId={file._id}
								setActiveActionId={() => setActiveActionId(file._id)}
							/>
							<Dropdown.Menu>
								{file._type !== 'Entry$Folder' && (
									<Dropdown.Item
										as="span"
										className="d-flex align-items-center align-content-center cursor-pointer pb-2"
										eventKey="previewFile"
									>
										<BsFileEarmarkPost className="me-2 text-700" />{' '}
										{t('preview')}
									</Dropdown.Item>
								)}
								{file._type === 'Entry$Folder' ? (
									<>
										<Dropdown.Item
											as="span"
											className="d-flex align-items-center align-content-center cursor-pointer py-2"
											eventKey="moveTo"
										>
											<BsArrowsMove className="me-2 text-700" /> {t('moveTo')}
										</Dropdown.Item>
										<Dropdown.Item
											as="span"
											className="d-flex align-items-center align-content-center cursor-pointer py-2"
											eventKey="edit"
										>
											<BsPencilFill className="me-2 text-700" /> {t('edit')}
										</Dropdown.Item>
										<Dropdown.Item
											as="span"
											className="d-flex align-items-center align-content-center cursor-pointer pt-2"
											eventKey="delete"
										>
											<BsTrash3 className="me-2 text-700" /> {t('delete')}
										</Dropdown.Item>
									</>
								) : (
								<>
								<Dropdown.Item
									as="span"
									className="d-flex align-items-center align-content-center cursor-pointer py-2"
									eventKey="visibility"
								>
									<span className="me-2">
										<VisibilityIcon isHidden={!file?.isHidden} />
									</span>
									{file?.isHidden ? t('show') : t('hide')}
								</Dropdown.Item>
								{file._type === 'Entry$File' && (
									<Dropdown.Item
										as="span"
										className="d-flex align-items-center align-content-center cursor-pointer py-2"
										eventKey="copyTo"
									>
										<MdContentCopy className="me-2 text-700" /> {t('copyTo')}
									</Dropdown.Item>
								)}
								<Dropdown.Item
									as="span"
									className="d-flex align-items-center align-content-center cursor-pointer py-2"
									eventKey="moveTo"
								>
									<BsArrowsMove className="me-2 text-700" /> {t('moveTo')}
								</Dropdown.Item>
								<Dropdown.Item
									as="span"
									className="d-flex align-items-center align-content-center cursor-pointer py-2"
									eventKey="rename"
								>
									<BsPencilFill className="me-2 text-700" /> {t('rename')}
								</Dropdown.Item>
								{file._type === 'Entry$File' && isVideoFile && (
									<Dropdown.Item
										as="span"
										className="d-flex align-items-center align-content-center cursor-pointer py-2"
										eventKey="editThumbnail"
									>
										<BsImageFill className="me-2 text-700" />{' '}
										{t('editThumbnail')}
									</Dropdown.Item>
								)}
								<Dropdown.Item
									as="span"
									className="d-flex align-items-center align-content-center cursor-pointer pt-2"
									eventKey="delete"
								>
									<BsTrash3 className="me-2 text-700" /> {t('delete')}
								</Dropdown.Item>
								</>
								)}
							</Dropdown.Menu>
						</Dropdown>
					</div>
				</div>
				<div className="px-4">
					<div
						className="bay-files-card-image d-flex justify-content-center align-items-center"
						style={{
							backgroundColor: 'var(--bs-100)',
							backgroundRepeat: 'no-repeat',
							backgroundSize: 'contain',
							backgroundPosition: 'center',
							width: '100%',
							height: '12em',
							maxHeight: '12em',
							borderRadius: 'var(--bs-border-radius)',
							opacity: file.isHidden ? 0.65 : 1,
						}}
					>
						{file._type === 'Entry$Folder' && (
							<div
								className="file-card-folder"
								onClick={() => handleOpenFolder(file)}
							>
								<div className="file-card-image-overlay">
									<Button
										className="mb-3 ribbon-select-button"
										value={file._id}
										variant="primary"
										onClick={() => handleOpenFolder(file)}
									>
										{t('open')}
									</Button>
								</div>
								{getIconByFileType('folder', 64)}
							</div>
						)}
						{file?._type === 'Entry$File' && !isVideoFile && (
							<img
								alt={file?.name}
								className="bay-files-card-image bay-files-card-content d-flex justify-content-center align-items-center"
								crossOrigin="anonymous"
								loading="lazy"
								src={imageSmushed}
								onClick={(event) =>
									handleActionClicked('previewFile', event, file)
								}
								onDragStart={(e) => e.preventDefault()}
							/>
						)}
						{file?._type === 'Entry$File' && isVideoFile && (
							<div className="position-relative w-100 h-100">
								<video
									className="bay-files-card-video d-flex justify-content-center align-items-center bay-files-card-content"
									crossOrigin="anonymous"
									height="100%"
									poster={
										file?.thumbnailUrls?.custom ||
										file?.thumbnailUrls?.default ||
										file?.thumbnailUrl
									}
									preload="metadata"
									width="100%"
									onClick={(event) =>
										handleActionClicked('previewFile', event, file)
									}
								>
									<source src={file?.url} type={file?.mimeType} />
								</video>
								<div
									className="position-absolute d-flex align-items-center justify-content-center"
									style={{
										top: 0,
										left: 0,
										right: 0,
										bottom: 0,
										pointerEvents: 'none',
										zIndex: 2,
									}}
								>
									<BsPlayCircleFill
										color="white"
										size={32}
										style={{
											filter: 'drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.5))',
											opacity: 0.9,
										}}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
				<div className=" px-4 pt-3 pb-4">
					<div className="d-flex align-items-center">
						<span className="position-relative" style={{ top: -1 }}>
							{getIconByFileType(
								file._type === 'Entry$File'
									? getFileType(file.mimeType)
									: 'folder',
								24,
							)}
						</span>
						<div className="ms-2 mb-0 text-truncate">
							<BayTooltip
								overlay={file.name}
								placement="top"
							>
								<h5
									className={`mb-0 ${file.isHidden ? 'text-500' : ''}`}
								>
									{file.name}
								</h5>
							</BayTooltip>
						</div>
					</div>
				</div>
			</Card>
		</div>
	);
};

interface FilesGridCardViewProps {
	t: (key: string) => string;
	noDataChildren?: React.ReactNode;
	filesList: FileData[];
	selectedEntriesArray: FileData[];
	dispatchSelect: (event: React.ChangeEvent<HTMLInputElement>, file: FileData) => void;
	handleOpenFolder: (file: FileData) => void;
	cardActionHandler: (action: string, file: FileData) => void;
	searchBreadcrumbs: (file: FileData) => React.ReactNode;
	isSearchView?: boolean;
	isLoading?: boolean;
	isReadyToShare?: boolean;
}

const FilesGridCardView: React.FC<FilesGridCardViewProps> = ({
	t,
	noDataChildren,
	filesList,
	selectedEntriesArray,
	dispatchSelect,
	handleOpenFolder,
	cardActionHandler,
	searchBreadcrumbs,
	isSearchView = false,
	isLoading = false,
}) => {
	const [activeActionId, setActiveActionId] = useState<string | null>(null);

	// REMOVED with the tooltip's conditional alignment: a `divideFactor` state, two
	// media queries, a `setFactorFunc`, and a ResizeObserver watching
	// `document.getElementById('ready_to_share')`.
	//
	// The whole mechanism existed to track how many cards fit in a row, for one
	// purpose: so FilesGridCard could compute `(index + 1) % divideFactor === 0` and
	// right-align the tooltip on the last card of each row to keep it in the viewport.
	// `shift({ padding: 8 })` in BayTooltip's middleware does that by MEASUREMENT, for
	// every card, without knowing the column count.
	//
	// Two things went with it that should not have been here anyway: a ResizeObserver
	// on every mount, and `document.getElementById('ready_to_share')` -- a consuming
	// app's element ID hardcoded inside bay-ui, which CLAUDE.md forbids ("bay-ui
	// components are dumb components"; it must never reach sideways into an app).
	//
	// If a future layout genuinely needs the column count, pass it in as a prop.

	return (
		<div className="bay-files-card-view px-0 container-fluid">
			{!isLoading ? (
				filesList?.length > 0 ? (
					<div className="row g-3 cards">
						{filesList?.map((file, index) => {
							return (
								<FilesGridCard
									key={index}
									activeActionId={activeActionId}
									cardActionHandler={cardActionHandler}
									dispatchSelect={dispatchSelect}
									file={file}
									handleOpenFolder={handleOpenFolder}
									index={index}
									isSearchView={isSearchView}
									searchBreadcrumbs={searchBreadcrumbs}
									selectedData={selectedEntriesArray}
									setActiveActionId={setActiveActionId}
									t={t}
								/>
							);
						})}
					</div>
				) : (
					<div className="bay-files-card-view-no-data box-shadow">
						<div className="text-center mt-2 bg-white p-5">
							{isSearchView && filesList?.length === 0
								? 'There are no files found matching your search.'
								: noDataChildren}
						</div>
					</div>
				)
			) : (
				<div className="bay-files-card-view-loading d-flex justify-content-center align-items-center">
					<BayLoader small />
				</div>
			)}
		</div>
	);
};

export default FilesGridCardView;
